import { type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';

import {
    clearAllSceneBuilding,
    clearAllAgentActivity,
    enterCreationStage,
    markSceneBlurred,
    markSceneTouchedByAgent,
    recordSceneFill,
    setSceneReviewing,
    skipReview,
    startSceneBuilding,
} from './agent-activity-actions';
import {
    CREATION_STAGE_ORDER,
    type CreationStage,
    type SceneFillProgress,
} from './agent-activity-types';

// Only consulted after a fill lands, never at creation: a design whose text slot ships
// blank reports no outstanding needs, so completing it on arrival would leave a
// brand-new scene showing its raw design. Such a scene waits for the turn to end.
function isFillComplete(progress: SceneFillProgress): boolean {
    return (
        (!progress.needsText || progress.text) &&
        (!progress.needsMedia || progress.media) &&
        (!progress.needsIcon || progress.icon)
    );
}

// A scene is "building" while it has an entry that is not yet `complete`.
export function sceneFillProgress(
    state: Immutable.Map<string, SceneFillProgress> = Immutable.Map(),
    action: AnyAction,
) {
    if (startSceneBuilding.match(action)) {
        const { sceneId, needsText, needsMedia, needsIcon } = action.payload;
        const entry: SceneFillProgress = {
            needsText,
            needsMedia,
            needsIcon,
            text: false,
            media: false,
            icon: false,
            complete: false,
        };
        return state.set(sceneId, entry);
    }
    if (recordSceneFill.match(action)) {
        const { sceneId, kind } = action.payload;
        const prev = state.get(sceneId);
        // Ignore fills on scenes we aren't tracking, e.g. later user edits.
        if (!prev || prev.complete) {
            return state;
        }
        const next: SceneFillProgress =
            kind === 'review' ? { ...prev, complete: true } : { ...prev, [kind]: true };
        return state.set(sceneId, {
            ...next,
            complete: next.complete || isFillComplete(next),
        });
    }
    // Either drops build tracking; a scene that never receives a fill needs this to escape.
    if (clearAllSceneBuilding.match(action) || clearAllAgentActivity.match(action)) {
        return state.clear();
    }
    return state;
}

// Every scene the agent touched this turn. Spans the whole turn, unlike `progress`, so
// the indicator doesn't flicker in the millisecond gaps between tool calls.
export function turnSceneIds(state: Immutable.Set<string> = Immutable.Set(), action: AnyAction) {
    if (startSceneBuilding.match(action)) {
        return state.add(action.payload.sceneId);
    }
    if (markSceneTouchedByAgent.match(action)) {
        return state.union(action.payload.sceneIds);
    }
    if (clearAllAgentActivity.match(action)) {
        return state.clear();
    }
    return state;
}

// Scenes image_ask is actively analyzing.
export function reviewingSceneIds(
    state: Immutable.Set<string> = Immutable.Set(),
    action: AnyAction,
) {
    if (setSceneReviewing.match(action)) {
        const { sceneIds, isReviewing } = action.payload;
        return isReviewing ? state.union(sceneIds) : state.subtract(sceneIds);
    }
    if (clearAllAgentActivity.match(action)) {
        return state.clear();
    }
    return state;
}

// Scenes that read as edited rather than building, but should still be blurred —
// voicedriven base scenes, which never receive a fill to clear them. Cleared with
// the rest of the turn.
export function blurredSceneIds(state: Immutable.Set<string> = Immutable.Set(), action: AnyAction) {
    if (markSceneBlurred.match(action)) {
        return state.union(action.payload.sceneIds);
    }
    if (clearAllAgentActivity.match(action)) {
        return state.clear();
    }
    return state;
}

// Latches for the rest of the turn: the per-scene `reviewing` set empties between
// image_ask calls, so on its own it can't tell "between batches" from "done reviewing".
// Unlike creationStage this is not gated on a build being underway — a follow-on edit
// that reviews still offers the skip, it just doesn't get the stage panel.
export function reviewStarted(state: boolean = false, action: AnyAction) {
    if (enterCreationStage.match(action) && action.payload.stage === 'reviewing') {
        return true;
    }
    if (clearAllAgentActivity.match(action)) {
        return false;
    }
    return state;
}

export function reviewSkipped(state: boolean = false, action: AnyAction) {
    if (skipReview.match(action)) {
        return true;
    }
    if (clearAllAgentActivity.match(action)) {
        return false;
    }
    return state;
}

/**
 * How far the current from-scratch build has got, or null when no build is underway.
 *
 * `planning` is the gate: only loading a video skill opens it, and that only happens
 * when building from scratch. A follow-on edit that reviews therefore stays null and
 * shows no stage panel — there is no multi-minute build to explain, just an edit.
 *
 * Only ever moves forward, so a second skill load can't drag the display back from
 * `building`, and tools can announce their own stage without coordinating.
 */
export function creationStage(state: CreationStage | null = null, action: AnyAction) {
    if (enterCreationStage.match(action)) {
        const { stage } = action.payload;
        if (stage === 'planning') {
            return state ?? 'planning';
        }
        if (state === null) {
            return null;
        }
        return CREATION_STAGE_ORDER.indexOf(stage) > CREATION_STAGE_ORDER.indexOf(state)
            ? stage
            : state;
    }
    if (clearAllAgentActivity.match(action)) {
        return null;
    }
    return state;
}

export const agentActivity = combineReducers({
    progress: sceneFillProgress,
    turn: turnSceneIds,
    reviewing: reviewingSceneIds,
    blurred: blurredSceneIds,
    reviewStarted,
    reviewSkipped,
    creationStage,
});

export type AgentActivityState = ReturnType<typeof agentActivity>;
